import type { MouseEvent } from 'react';
import type { MethodologyMarker } from '@aithora/shared';

export interface TrajSeries {
  key: string;
  name: string;
  pts: number[];
  on: boolean;
  color: string;
}

/** Each line's last-three-quarter slope carried a third of a quarter (30 days) forward. MODELED. */
export function forecast(pts: number[]): number {
  const n = pts.length;
  return Math.max(0, Math.min(100, pts[n - 1] + (pts[n - 1] - pts[Math.max(0, n - 4)]) / 3 / 3));
}

const FONT = 'Archivo, system-ui, sans-serif';

/**
 * The composite trajectory (design lineChart, L33938): one line per building, the
 * portfolio min to max envelope, the methodology-change verticals, the peer median, and
 * the +30d MODELED strip. Hover reads a quarter; a tap pins it (a second tap releases).
 */
export function TrajectoryChart({
  series,
  quarters,
  peerMed,
  markers,
  hover,
  pinned,
  onHover,
  onPin,
}: {
  series: TrajSeries[];
  quarters: string[];
  peerMed: number;
  markers: MethodologyMarker[];
  hover: number | null;
  pinned: number | null;
  onHover: (q: number | null) => void;
  onPin: (q: number | null) => void;
}) {
  const W = 1000;
  const H = 540;
  const mL = 44;
  const mR = 96;
  const mT = 28;
  const mB = 36;
  const n = quarters.length;
  const fc = series.map((s) => forecast(s.pts));
  let dMin = 100;
  let dMax = 0;
  series.forEach((s) =>
    s.pts.forEach((p) => {
      if (p < dMin) dMin = p;
      if (p > dMax) dMax = p;
    }),
  );
  fc.forEach((p) => {
    if (p < dMin) dMin = p;
    if (p > dMax) dMax = p;
  });
  dMin = Math.min(dMin, peerMed);
  dMax = Math.max(dMax, peerMed);
  const lo = Math.max(0, Math.floor((dMin - 4) / 5) * 5);
  const hi = Math.min(100, Math.ceil((dMax + 4) / 5) * 5);
  const step = (hi - lo) / 4;
  const ticks = [0, 1, 2, 3, 4].map((i) => Math.round(lo + step * i));
  const x = (i: number) => mL + i * ((W - mL - mR) / (n - 1));
  const y = (v: number) => mT + (1 - (v - lo) / (hi - lo)) * (H - mT - mB);
  const anySel = series.some((s) => s.on);

  const mins: number[] = [];
  const maxs: number[] = [];
  for (let i = 0; i < n; i++) {
    const col = series.map((s) => s.pts[i]);
    mins.push(Math.min(...col));
    maxs.push(Math.max(...col));
  }
  const env =
    maxs.map((v, i) => (i ? 'L' : 'M') + x(i).toFixed(1) + ' ' + y(v).toFixed(1)).join(' ') +
    ' ' +
    mins
      .slice()
      .reverse()
      .map((v, i) => 'L' + x(n - 1 - i).toFixed(1) + ' ' + y(v).toFixed(1))
      .join(' ') +
    ' Z';
  const fx = x(n - 1) + 62;

  const qAt = (e: MouseEvent<SVGSVGElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    const vx = r.width ? ((e.clientX - r.left) / r.width) * W : mL;
    const i = Math.round((vx - mL) / ((W - mL - mR) / (n - 1)));
    return Math.max(0, Math.min(n - 1, i));
  };

  return (
    <svg
      width="100%"
      height="auto"
      viewBox={'0 0 ' + W + ' ' + H}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label="Composite trajectory"
      data-testid="traj-chart"
      onMouseMove={(e) => {
        const i = qAt(e);
        if (i !== hover) onHover(i);
      }}
      onMouseLeave={() => {
        if (hover !== pinned) onHover(pinned);
      }}
      onClick={(e) => {
        const i = qAt(e);
        if (pinned === i) {
          onPin(null);
          onHover(null);
        } else {
          onPin(i);
          onHover(i);
        }
      }}
      style={{ display: 'block', cursor: 'crosshair' }}
    >
      {ticks.map((g) => [
        <line key={'g' + g} x1={mL} x2={W - mR} y1={y(g)} y2={y(g)} stroke="rgba(34,39,46,.08)" strokeWidth={1} />,
        <text key={'gt' + g} x={mL - 9} y={y(g) + 4} textAnchor="end" fontSize={14} fill="#54606C" fontFamily={FONT}>
          {String(g)}
        </text>,
      ])}
      {series.length > 1 && <path d={env} fill="rgba(34,39,46,.045)" />}
      {quarters.map((q, i) => (
        <text key={'q' + i} x={x(i)} y={H - 11} textAnchor="middle" fontSize={14} fill={hover === i ? '#22272E' : '#54606C'} fontWeight={hover === i ? 700 : 400} fontFamily={FONT}>
          {q}
        </text>
      ))}
      {markers.map((m) => [
        <line key={'m' + m.quarterIndex} x1={x(m.quarterIndex)} x2={x(m.quarterIndex)} y1={mT} y2={H - mB} stroke="rgba(34,39,46,.22)" strokeWidth={1} strokeDasharray="2 4" />,
        <text key={'ml' + m.quarterIndex} x={x(m.quarterIndex) - 5} textAnchor="end" y={mT - 9} fontSize={14} fill="#54606C" fontFamily={FONT}>
          {m.label}
        </text>,
        <rect key={'mh' + m.quarterIndex} x={x(m.quarterIndex) - 8} y={mT - 22} width={16} height={H - mB - mT + 22} fill="transparent" style={{ cursor: 'help' }}>
          <title>{m.label + ': CMS changed how this is scored, so quarters either side of this line are not strictly comparable.'}</title>
        </rect>,
      ])}
      <line x1={mL} x2={W - mR} y1={y(peerMed)} y2={y(peerMed)} stroke="#5B6B7C" strokeWidth={1.4} strokeDasharray="5 4" />
      <text x={mL + 6} y={y(peerMed) - 6} fontSize={14} fill="#5B6B7C" fontWeight={600}>
        {'Peer median ' + peerMed}
      </text>
      {hover !== null && <line x1={x(hover)} x2={x(hover)} y1={mT} y2={H - mB} stroke="rgba(47,92,151,.45)" strokeWidth={1.5} />}
      <rect x={x(n - 1)} y={mT} width={fx - x(n - 1) + 14} height={H - mT - mB} fill="rgba(47,92,151,.05)" />
      <text x={W - 2} y={mT - 9} textAnchor="end" fontSize={13} fill="#264C7E" fontWeight={700} letterSpacing=".06em" fontFamily={FONT}>
        +30d MODELED
      </text>
      <text x={fx} y={H - 11} textAnchor="middle" fontSize={14} fill="#54606C" fontFamily={FONT}>
        +30d
      </text>
      {series.map((s, si) => {
        const dimF = anySel && !s.on;
        return [
          <path
            key={'f' + si}
            d={'M' + x(n - 1).toFixed(1) + ' ' + y(s.pts[n - 1]).toFixed(1) + ' L' + fx.toFixed(1) + ' ' + y(fc[si]).toFixed(1)}
            fill="none"
            stroke={dimF ? 'rgba(34,39,46,.14)' : s.color}
            strokeWidth={s.on ? 2.4 : 1.5}
            strokeDasharray="3 4"
            strokeLinecap="round"
          />,
          <circle key={'fc' + si} cx={fx} cy={y(fc[si])} r={s.on ? 4.4 : 3} fill="#FCFAF4" stroke={dimF ? 'rgba(34,39,46,.2)' : s.color} strokeWidth={1.6} />,
        ];
      })}
      {series.map((s, si) => {
        const dim = anySel && !s.on;
        const d = s.pts.map((p, i) => (i ? 'L' : 'M') + x(i).toFixed(1) + ' ' + y(p).toFixed(1)).join(' ');
        return [
          <path
            key={'l' + si}
            d={d}
            fill="none"
            strokeLinejoin="round"
            strokeLinecap="round"
            stroke={dim ? 'rgba(34,39,46,.16)' : s.color}
            strokeWidth={s.on ? 3.8 : 2.6}
            pathLength={1}
            strokeDasharray={1}
            style={{ animation: 'drawLine 1.1s cubic-bezier(.22,.61,.36,1) ' + si * 70 + 'ms both' }}
          />,
          <circle key={'e' + si} cx={x(n - 1)} cy={y(s.pts[n - 1])} r={s.on ? 4.6 : 3} fill={dim ? 'rgba(34,39,46,.2)' : s.color} />,
          hover !== null && !dim ? <circle key={'h' + si} cx={x(hover)} cy={y(s.pts[hover])} r={s.on ? 5 : 3.6} fill={s.color} stroke="#FCFAF4" strokeWidth={1.5} /> : null,
        ];
      })}
    </svg>
  );
}
